import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { isModuleImportError, formatError } from '@/lib/utils'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isModuleError: boolean
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, isModuleError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    const isModule = isModuleImportError(error)
    return { hasError: true, error, isModuleError: isModule }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)

    // Auto-reload for module import errors (stale cache after deploy)
    if (isModuleImportError(error)) {
      console.info('[ErrorBoundary] Module import error detected, auto-reloading...')
      // Small delay to allow the error to be logged
      setTimeout(() => window.location.reload(), 1000)
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, isModuleError: false })
  }

  render() {
    if (this.state.hasError) {
      // Special UI for module import errors
      if (this.state.isModuleError) {
        return (
          <div className="w-full min-h-[400px] flex items-center justify-center p-4">
            <Alert className="max-w-md bg-card border-amber-500/30 shadow-2xl rounded-2xl p-8">
              <RefreshCw className="h-5 w-5 text-amber-500 animate-spin" />
              <AlertTitle className="text-xl font-display font-bold text-amber-400">Atualizando Sistema</AlertTitle>
              <AlertDescription className="mt-2 text-muted-foreground text-sm">
                Uma nova versão foi detectada. Recarregando automaticamente...
              </AlertDescription>
              <Button
                onClick={this.handleReload}
                className="mt-4 bg-amber-600 hover:bg-amber-500 text-white"
                size="sm"
              >
                Recarregar Agora
              </Button>
            </Alert>
          </div>
        )
      }

      return this.props.fallback || (
        <div className="w-full min-h-[400px] flex items-center justify-center p-4">
          <Alert variant="destructive" className="max-w-md bg-card border-destructive/30 shadow-2xl rounded-2xl p-8">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle className="text-xl font-display font-bold">System Malfunction</AlertTitle>
            <AlertDescription className="mt-2 text-muted-foreground text-sm">
              {formatError(this.state.error) || 'An unexpected error occurred in the neural mesh.'}
            </AlertDescription>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={this.handleReset}
                className="bg-primary hover:bg-rsb-accent-glow text-white shadow-glow-sm"
                size="sm"
              >
                Reinitialize System
              </Button>
              <Button
                onClick={this.handleReload}
                variant="outline"
                size="sm"
              >
                Reload Page
              </Button>
            </div>
          </Alert>
        </div>
      )
    }
    return this.props.children
  }
}
